import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import { AuthState } from "slices/auth/reducer";
import { CollaborationsSlice, CollaborationTasksSummary, MilestoneData, SocialPost } from "types/campaign/collaborations";
import { CollaborationMilestoneSlice } from "./reducer";
import { getErrorMessage, isFresh } from "utils/helpers";
import { fetchCollaborationMilestone, fetchCollaborationPost, fetchCollaborationTasksSummary } from "services/api/campaign/collaboration";
import { ERROR } from "types";
import { CollaborationChatRoom } from "types/campaign/collaborations/chat";
import { getCollabChatRoom } from "services/api/chat";
import { influencerSlice } from "types/influencer";

export const fetchCollaborationMilestoneData = createAsyncThunk<{data: MilestoneData, influencerId: string, campaignId: string, milestoneId: string, cache?: boolean}, {campaignId: string, milestoneId: string, influencerId: string, force?: boolean}, {rejectValue: string}>(
    'campaign/collaboration/milestone',
    async ({campaignId, influencerId, milestoneId, force}, { getState, rejectWithValue }) => {
        try { 
            const STALE_TIME = apiConfig.FETCH_COLLABORATION_MILESTONE.staleTime;
            const state = getState() as {auth: AuthState, collaborationMilestones: CollaborationMilestoneSlice};
            const existingData = state.collaborationMilestones.collaborations[campaignId]?.[influencerId]?.[milestoneId] ?? null
            if(!force && isFresh(existingData?.last_fetched_at ?? null, STALE_TIME)){
                return {milestoneId, campaignId, influencerId, data: existingData.data, cache: true}
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            const data = await fetchCollaborationMilestone(agencyId, campaignId, influencerId, milestoneId);
            return {milestoneId, campaignId, influencerId, data};
        } catch (error) {
            return rejectWithValue('Failed to fetch collaboration milestone');
        }
    }
)

export const fetchCollaborationChatRoom = createAsyncThunk<{room: CollaborationChatRoom, influencerId: string, campaignId: string, cache?: boolean}, {campaignId: string, influencerId: string, force?: boolean}, {rejectValue: string}>(
    'campaign/collaboration/chatroom',
    async ({campaignId, influencerId, force}, { getState, rejectWithValue }) => {
        try { 
            const STALE_TIME = apiConfig.FETCH_COLLABORATION_MILESTONE.staleTime;
            const state = getState() as {auth: AuthState, collaborationMilestones: CollaborationMilestoneSlice};
            const existingData = state.collaborationMilestones.chatRooms[campaignId]?.[influencerId] ?? null
            if(!force && isFresh(existingData?.last_fetched_at ?? null, STALE_TIME)){
                return {campaignId, influencerId, room: existingData.room, cache: true}
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            const room = await getCollabChatRoom(agencyId, campaignId, influencerId);
            return {campaignId, influencerId, room, cache: false};
        } catch (error) {
            return rejectWithValue('Failed to fetch collaboration chatroom');
        }
    }
)

export const fetchCollaborationTasksSummaryThunk = createAsyncThunk<{ summary: CollaborationTasksSummary }, { force?: boolean }, { rejectValue: string }>(
    'collaboration/task/summary',
    async ({ force }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { auth: AuthState, collaborationMilestones: CollaborationMilestoneSlice };
            const STALE_TIME = apiConfig.FETCH_COLLABS_SUMMARY.staleTime;
            const selectedAgencyId = state.auth.selectedAgencyId;

            if(!selectedAgencyId) throw new Error("Agency not selected!");

            if (!force && isFresh(state.collaborationMilestones.summary.last_fetched_at, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }

            const summary = await fetchCollaborationTasksSummary(selectedAgencyId);

            return { summary }

        } catch (error) {
            console.log('asdfasdfas', error)
            return rejectWithValue(getErrorMessage(error, "Failed to fetch collaboration task summary!"));
        }
    }
)


export const fetchCollaborationPostThunk = createAsyncThunk<{collaborationId: string, social_post: SocialPost, media_url: string, postId: string}, {force?: boolean, collaborationId: string, postId: string}, {rejectValue: string}>(
    'collaboration/social-post',
    async ({force, collaborationId, postId}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_SOCIAL_POST.staleTime;
            const state = getState() as {auth: AuthState, collaborations: CollaborationsSlice};
            const agencyId = state.auth.selectedAgencyId ?? '';
            if(!agencyId) throw new Error("No agency id selected!");
            if(!force && isFresh(state.collaborations.collaborationPosts[collaborationId]?.[postId]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }
            const {media_url, social_post} = await fetchCollaborationPost(agencyId, collaborationId, postId);

            return {social_post, media_url, postId, collaborationId}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch post"));
        }
    }
) 
